import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '@/components/atoms/Button';
import BasicTabs from '@/components/molecules/BasicTabs';
import ExtraTripCard, {
	ExtraTripCardRef,
} from '@/components/molecules/ExtraTripCard';
import NewExtraTripModal from '@/components/organisms/NewExtraTripModal';
import api from '@/services/apiSgft';
import { GetEmployeeExtraTripSolicitationsResponseDto } from '@/types/extra-trip-solicitation.types';

const ExtraTrips: React.FC = () => {
	const { employeeId, extraTripId } = useParams<{
		employeeId: string;
		extraTripId: string;
	}>();
	const navigate = useNavigate();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | number | null>(null);
	const [activeTab, setActiveTab] = useState(0);

	const fetchExtraTrips = async (
		type: 'scheduled' | 'history',
	): Promise<GetEmployeeExtraTripSolicitationsResponseDto> => {
		if (!employeeId) {
			return { employeeName: '-', solicitations: [] };
		}
		try {
			const response =
				await api.get<GetEmployeeExtraTripSolicitationsResponseDto>(
					`/extra-trip-solicitation/employee/${employeeId}`,
					{
						params: { type },
					},
				);
			return response.data;
		} catch (error) {
			toast.error(
				type === 'scheduled'
					? 'Erro ao carregar idas extras programadas'
					: 'Erro ao carregar histórico de idas extras',
				{
					theme: 'colored',
					toastId: 'error',
				},
			);
			throw error;
		}
	};

	const handleCancel = async (id: string | number) => {
		await api.post(`/extra-trip-solicitation/${id}/cancel`);
	};

	const handleCancelDay = async (
		solicitationId: string | number,
		dayId: string,
	) => {
		await api.post(
			`/extra-trip-solicitation/${solicitationId}/days/${dayId}/cancel`,
		);
	};

	const {
		data: scheduledData,
		isLoading: isLoadingScheduled,
		refetch: refetchScheduled,
	} = useQuery(
		['extra-trips-scheduled', employeeId],
		() => fetchExtraTrips('scheduled'),
		{
			enabled: !!employeeId,
			retry: false,
		},
	);

	const {
		data: historyData,
		isLoading: isLoadingHistory,
		refetch: refetchHistory,
	} = useQuery(
		['extra-trips-history', employeeId],
		() => fetchExtraTrips('history'),
		{
			enabled: !!employeeId,
			retry: false,
		},
	);

	const scheduledTrips = scheduledData?.solicitations || [];
	const historyTrips = historyData?.solicitations || [];
	const employeeName =
		scheduledData?.employeeName || historyData?.employeeName || '';

	const cardRefs = useRef<Map<string | number, ExtraTripCardRef>>(new Map());
	const isInitialLoadRef = useRef(true);
	const userInteractionRef = useRef(false);

	const findCardTab = useCallback(
		(extraTripId: string): { isInScheduled: boolean; isInHistory: boolean } => {
			const tripIdNumber = Number(extraTripId);
			const isInScheduled = scheduledTrips.some(
				(trip) => trip.id === tripIdNumber,
			);
			const isInHistory = historyTrips.some((trip) => trip.id === tripIdNumber);
			return { isInScheduled, isInHistory };
		},
		[scheduledTrips, historyTrips],
	);

	const switchToCorrectTab = useCallback(
		(isInScheduled: boolean, isInHistory: boolean) => {
			if (isInScheduled) {
				setActiveTab(0);
			} else if (isInHistory) {
				setActiveTab(1);
			}
		},
		[],
	);

	const scrollToCard = useCallback((tripId: string | number) => {
		const timeoutId = setTimeout(() => {
			const cardRef = cardRefs.current.get(tripId);
			if (cardRef) {
				cardRef.scrollIntoView();
			}
		}, 200);
		return () => clearTimeout(timeoutId);
	}, []);

	useEffect(() => {
		if (!extraTripId || isLoadingScheduled || isLoadingHistory) {
			if (!extraTripId && isInitialLoadRef.current) {
				isInitialLoadRef.current = false;
			}
			return;
		}

		const tripId = Number(extraTripId);
		const { isInScheduled, isInHistory } = findCardTab(extraTripId);

		if (!isInScheduled && !isInHistory) {
			userInteractionRef.current = false;
			return;
		}

		switchToCorrectTab(isInScheduled, isInHistory);
		if (isInitialLoadRef.current && !userInteractionRef.current) {
			isInitialLoadRef.current = false;
			return scrollToCard(tripId);
		}

		userInteractionRef.current = false;
	}, [
		extraTripId,
		isLoadingScheduled,
		isLoadingHistory,
		findCardTab,
		switchToCorrectTab,
		scrollToCard,
	]);

	if (!employeeId) {
		return (
			<div className="flex h-[90vh] w-full items-center justify-center rounded-md bg-white p-3">
				<p className="text-gray-600">ID do colaborador não encontrado</p>
			</div>
		);
	}

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setEditingId(null);
		refetchScheduled();
		refetchHistory();
	};

	const handleEdit = (id: string | number) => {
		setEditingId(id);
		setIsModalOpen(true);
	};

	const handleCardExpand = (tripId: string | number) => {
		userInteractionRef.current = true;
		navigate(`/home/colaborador/${employeeId}/idas-extras/${tripId}`, {
			replace: true,
		});
	};

	const handleCardCollapse = () => {
		userInteractionRef.current = true;
		navigate(`/home/colaborador/${employeeId}/idas-extras`, {
			replace: true,
		});
	};

	return (
		<div className="h-[90vh] w-full flex-col rounded-md bg-white p-3 overflow-y-hidden">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<h2 className="text-xl font-semibold text-primary">Idas Extras</h2>
					{employeeName && employeeName !== '-' && (
						<p className="text-sm text-primary">{employeeName}</p>
					)}
				</div>
				<Button
					type="button"
					onClick={() => setIsModalOpen(true)}
					className="rounded-full bg-primary text-white hover:bg-primary/90"
				>
					Nova Ida Extra
				</Button>
			</div>

			<BasicTabs
				value={activeTab}
				background="transparent"
				tabsNames={['Idas Extras Programadas', 'Histórico']}
				onChange={handleTabChange}
				height="91%"
				tabs={[
					<div key={0} className="flex w-full flex-col gap-4 pt-2">
						{isLoadingScheduled ? (
							<div className="flex h-64 items-center justify-center">
								<p className="text-gray-600">Carregando...</p>
							</div>
						) : scheduledTrips.length === 0 ? (
							<div className="flex h-64 items-center justify-center">
								<p className="text-gray-600">
									Nenhuma ida extra programada encontrada
								</p>
							</div>
						) : (
							scheduledTrips.map((trip) => {
								const tripId = trip.id;
								const isTargetTrip =
									extraTripId && tripId === Number(extraTripId);

								return (
									<ExtraTripCard
										key={trip.id}
										ref={(ref) => {
											if (ref) {
												cardRefs.current.set(tripId, ref);
											} else {
												cardRefs.current.delete(tripId);
											}
										}}
										{...trip}
										onEdit={handleEdit}
										onCancel={handleCancel}
										onCancelDay={handleCancelDay}
										defaultExpanded={isTargetTrip || false}
										onExpand={() => handleCardExpand(tripId)}
										onCollapse={handleCardCollapse}
									/>
								);
							})
						)}
					</div>,
					<div key={1} className="flex w-full flex-col gap-4 pt-2">
						{isLoadingHistory ? (
							<div className="flex h-64 items-center justify-center">
								<p className="text-gray-600">Carregando...</p>
							</div>
						) : historyTrips.length === 0 ? (
							<div className="flex h-64 items-center justify-center">
								<p className="text-gray-600">
									Nenhuma ida extra no histórico encontrada
								</p>
							</div>
						) : (
							historyTrips.map((trip) => {
								const tripId = trip.id;
								const isTargetTrip =
									extraTripId && tripId === Number(extraTripId);

								return (
									<ExtraTripCard
										key={trip.id}
										ref={(ref) => {
											if (ref) {
												cardRefs.current.set(tripId, ref);
											} else {
												cardRefs.current.delete(tripId);
											}
										}}
										{...trip}
										defaultExpanded={isTargetTrip || false}
										onExpand={() => handleCardExpand(tripId)}
										onCollapse={handleCardCollapse}
										isHistoryView
									/>
								);
							})
						)}
					</div>,
				]}
			/>

			<NewExtraTripModal
				employeeId={employeeId}
				modalIsOpen={isModalOpen}
				handleClose={handleModalClose}
				editingId={editingId}
			/>
		</div>
	);
};

export default ExtraTrips;
